// apps/web/src/wizard/steps/A5GuestStep.tsx — Phase 4 plan 04-03.
//
// Guest Contact + Consent step (A5 in the wireframe, step 4 of 6
// in the progress segments). The page renders:
//   - the contact form (name + email/phone)
//   - the DSGVO consent block (operator text → bundle default)
// Validation runs locally before the "Weiter" button enables; the
// `consentAt` timestamp is captured at the checkbox-click moment
// and persisted on the wizard state so A6 can submit it verbatim
// (D-15 / DSG-05).
//
// The consent text comes from `Query.restaurant`; the locale-aware
// fallback to `CONSENT_DEFAULTS` (apps/web/src/lib/consent-defaults.ts)
// is applied when the API returns null/empty in both DE and EN.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import {
  ContactForm,
  type ContactFormErrors,
} from "../../components/contact-form";
import { ConsentCheckbox } from "../../components/consent-checkbox";
import { getRestaurant } from "../../lib/api";
import { CONSENT_DEFAULTS } from "../../lib/consent-defaults";
import { captureConsent, setGuest, useWizardSlice } from "../state";
import {
  validateConsent,
  validateGuestForm,
  type ConsentCapture,
  type GuestFormInput,
} from "../schema";
import { issuesToMessages } from "../validation";

export function A5GuestStep(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language.startsWith("en") ? "en" : "de";
  const serviceDate = useWizardSlice((s) => s.serviceDate);
  const selectedSlot = useWizardSlice((s) => s.selectedSlot);
  const guest = useWizardSlice((s) => s.guest);

  // Local errors are derived from the latest form snapshot; we
  // don't run the validator on every keystroke (that would show
  // errors before the user has finished typing), but we DO show
  // errors after a submit attempt via `attempted`.
  const [attempted, setAttempted] = useState(false);
  const [formValues, setFormValues] = useState<GuestFormInput>({
    name: guest.name,
    email: guest.email,
    phone: guest.phone,
  });

  const restaurantQuery = useQuery({
    queryKey: ["wizard-restaurant"],
    queryFn: () => getRestaurant(),
    staleTime: 60_000,
  });

  const consentText = useMemo(() => {
    const op = restaurantQuery.data?.consentText;
    if (op) {
      const primary = locale === "de" ? op.de : op.en;
      if (typeof primary === "string" && primary.trim().length > 0) {
        return primary;
      }
      const fallback = locale === "de" ? op.en : op.de;
      if (typeof fallback === "string" && fallback.trim().length > 0) {
        return fallback;
      }
    }
    return CONSENT_DEFAULTS[locale];
  }, [restaurantQuery.data, locale]);

  // Pre-flight guards.
  if (!serviceDate) {
    navigate("/reservieren/datum", { replace: true });
    return <></>;
  }
  if (!selectedSlot) {
    navigate("/reservieren/zeit", { replace: true });
    return <></>;
  }

  const guestValidation = validateGuestForm(formValues);
  const consentCapture: ConsentCapture = {
    consentText,
    consentAt: guest.consentAt ?? "",
    accepted: guest.accepted,
  };
  const consentValidation = validateConsent(consentCapture);

  const isValid = guestValidation.ok && consentValidation.ok;

  const fieldErrors: ContactFormErrors = attempted
    ? {
        ...(issuesToMessages(guestValidation, t) as Record<string, string>),
        contactRequired: guestValidation.issues.some(
          (i) =>
            i.path === "email" && i.key === "wizard.a5.errors.contactRequired",
        )
          ? t("wizard.a5.errors.contactRequired")
          : undefined,
      }
    : {};

  const consentErrors = attempted
    ? {
        consentText: consentValidation.issues.find(
          (i) => i.path === "consentText",
        )
          ? t("wizard.a5.errors.consentEmpty")
          : undefined,
        accepted: consentValidation.issues.find((i) => i.path === "accepted")
          ? t("wizard.a5.errors.consentRequired")
          : undefined,
      }
    : {};

  function next() {
    setAttempted(true);
    if (!isValid) return;
    navigate("/reservierung/uebersicht");
  }

  function back() {
    navigate("/reservieren/zeit");
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a5-guest-step"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
          data-testid="a5-header"
        >
          <button
            type="button"
            onClick={back}
            aria-label={t("wizard.a5.back")}
            data-testid="a5-back"
            className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-1">
            <h1
              className="font-heading text-2xl md:text-3xl"
              data-testid="a5-heading"
            >
              {t("wizard.a5.title")}
            </h1>
            <p
              className="text-sm text-text/70 mt-0.5"
              data-testid="a5-subtitle"
            >
              {t("wizard.a5.subtitle")}
            </p>
          </div>
        </header>

        <div className="px-4 pt-4">
          <p className="text-xs text-text/70 mb-2" data-testid="a5-meta">
            Schritt 4 von 6
          </p>
          <WizardProgress current={4} />
        </div>

        <div className="px-4 pt-4 pb-4 space-y-4">
          <ContactForm
            defaultValue={formValues}
            onChange={(v) => {
              setFormValues(v);
              setGuest(v);
            }}
            errors={fieldErrors}
          />

          <ConsentCheckbox
            consentText={consentText}
            checked={guest.accepted}
            onCheck={(checked, consentAt) => {
              captureConsent({ consentText, consentAt });
              if (!checked) {
                // Unchecking clears the captured timestamp so the
                // user must re-accept (and the consentAt re-anchors
                // to the new click).
                setGuest({ accepted: false, consentAt: null });
              }
            }}
            errors={consentErrors}
          />
        </div>

        <div className="px-4 pb-6">
          <button
            type="button"
            onClick={next}
            disabled={!isValid && attempted}
            data-testid="a5-next"
            className={`w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
              isValid
                ? "border-accent text-accent-700 hover:bg-accent-100"
                : attempted
                  ? "border-red-500 text-red-700"
                  : "border-divider text-text/40"
            }`}
          >
            <span>{t("wizard.a5.next")}</span>
          </button>
        </div>
      </div>
    </main>
  );
}

function WizardProgress({ current }: { current: number }): JSX.Element {
  const { t } = useTranslation();
  return (
    <ol
      role="list"
      aria-label={t("reservation.wizard.progressAria", {
        current,
        total: 6,
      })}
      className="flex gap-1 mb-4"
      data-testid="a5-wizard-progress"
      data-wizard-current={current}
    >
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <li
          key={n}
          aria-current={n === current ? "step" : undefined}
          className={`h-[3px] flex-1 rounded-sm ${
            n <= current ? "bg-accent" : "bg-divider"
          }`}
          data-testid={`a5-wizard-segment-${n}`}
          data-filled={n <= current}
        />
      ))}
    </ol>
  );
}
